<template>
  <Background
    v-if="displayBlock"
    :background
    class="overflow-hidden"
    :custom-background-image-class="
      hasBackgroundImage
        ? 'bg-top bg-center xs:bg-[length:min(180%,768px)] sm:bg-[length:120%] md:bg-[length:auto_100%] md:bg-right bg-no-repeat'
        : undefined
    "
    :custom-overlay-class="
      isLight ? $style.beigeGradient : $style.blackGradient
    "
    rounded="always"
  >
    <div
      class="w-full md:min-h-[472px] md:mt-0 flex flex-col py-24 pl-24 pr-24 md:pl-48 justify-center"
      :class="{ 'mt-[128px]': hasBackgroundImage }"
    >
      <section class="grid grid-cols-1 md:grid-cols-2 gap-24 md:gap-32">
        <div class="flex flex-col justify-center">
          <RevTag
            v-if="tag"
            class="mood-main w-fit mb-8"
            :label="tag"
            size="large"
            variant="primary"
          />
          <p v-if="eyebrowText && !tag" class="mb-8 body-2">
            {{ eyebrowText }}
          </p>

          <RichText
            class="punchline hyphens-auto mb-8 md:mb-20"
            :node-renderers-overrides="renderParagraphAsHeading"
            :rich-text="title"
            style-variant="none"
          />

          <RichText
            v-if="subtitle"
            class="body-2 md:heading-3 mb-24 md:mb-32 hyphens-auto"
            :rich-text="subtitle"
            style-variant="none"
          />

          <component
            :is="ctaList.length > 1 ? 'ul' : 'div'"
            v-if="ctaList && ctaList.length"
            ref="spotlight-cta-scroller"
            class="flex gap-10 lg:items-center -mr-24 flex-nowrap overflow-x-auto scrollbar-none pr-24 lg:mr-0 lg:flex-wrap lg:pr-0"
            :class="maskClass"
            data-test="spotlight-cta-wrapper"
          >
            <ProductSpotlightCTA
              v-for="(cta, index) in ctaList"
              :key="`${cta.label}-${index}`"
              :ariaLabelledBy="titleId"
              :multiple="ctaList.length > 1"
              v-bind="cta"
              :tracking="{ ...tracking, ctaPosition: index + 1 }"
            />
          </component>
        </div>

        <ProductCardCarouselCard
          v-if="spotlightProduct"
          class="mood-main self-center text-static-default-hi w-full !h-fit md:max-w-256 justify-self-center md:justify-self-end"
          :index="0"
          :product="spotlightProduct"
          :product-granularity="productGranularity ?? 'variant'"
          :tracking="{
            ...tracking,
            productComponent: 'ProductSpotlight',
            list: trackingList,
          }"
        />
      </section>
    </div>
  </Background>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, h, useTemplateRef } from 'vue'

import type { ProductSpotlightApiProps } from '@backmarket/http-api/src/api-specs-content/models/product-spotlight'
import { useDiscountedPrice } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useDiscountedPrice'
import ProductCardCarouselCard from '@backmarket/nuxt-layer-recommendation/ProductCardCarouselCard.vue'
import { useAddProductsCacheTagHeader } from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'
import { tw } from '@backmarket/utils/string/tw'
import { RevTag } from '@ds/components/Tag'
import type { NodeRendererMap } from 'contentful-rich-text-vue-renderer'

import { useScrollMaskClass } from '../../composables/useScrollMaskClass'
import type { BlockProps } from '../../models/content-block'
import { useSearchProducts } from '../../products/useSearchProducts'
import { isImageBackground } from '../../shared-components/Background/Background.utils'
import Background from '../../shared-components/Background/Background.vue'
import { textToTarget } from '../../utils/textToTarget'
import RichText from '../RichText/RichText.vue'
import { findTitlesInRichtext } from '../RichText/RichTextVueParser.utils'

import ProductSpotlightCTA from './ProductSpotlightCTA.vue'

export type ProductSpotlightBlockProps = ProductSpotlightApiProps & BlockProps

const {
  eyebrowText,
  tag,
  title,
  subtitle,
  productFilters,
  background = { theme: 'black' },
  productGranularity,
  ctaList,
  tracking,
} = defineProps<ProductSpotlightBlockProps>()

const hasBackgroundImage = isImageBackground(background)
const isLight = hasBackgroundImage ? background.mode === 'light' : false
const titleId = findTitleId()

const productCategoriesScrollerRef = useTemplateRef<HTMLDivElement>(
  'spotlight-cta-scroller',
)
const { maskClass } = useScrollMaskClass(productCategoriesScrollerRef)
const route = useRoute()

const { data: spotlightProducts, pending } = useSearchProducts(
  { productQuery: productFilters },
  { immediate: true, server: true },
)
const { products: parsedSpotlightProducts } =
  useDiscountedPrice(spotlightProducts)

useAddProductsCacheTagHeader({ products: spotlightProducts })

const spotlightProduct = computed(() => {
  if (!parsedSpotlightProducts.value) return undefined

  return parsedSpotlightProducts.value[0]
})

const displayBlock = computed(() => {
  if (pending.value || !spotlightProduct.value) return false
  if (!spotlightProduct.value.availableStock) return false

  return true
})

const trackingList = computed(() => {
  const { name, params } = route
  const pageName = params?.pageName ?? name

  return String(tracking?.zone ?? pageName)
})

// render the title as a h2 tag
const renderParagraphAsHeading: NodeRendererMap = {
  paragraph: (node, key, next) => {
    return h(
      'h2',
      { key, class: tw`empty:hidden`, id: titleId },
      next(node.content, key, next),
    )
  },
}

function findTitleId() {
  const contentItems = title.content?.[0]?.content || []
  const nodeType = title.content?.[0]?.nodeType
  const titles = findTitlesInRichtext(contentItems, nodeType, { merge: true })

  return titles.length ? textToTarget(titles[0].title, nodeType) : ''
}
</script>

<style lang="css" module>
.beigeGradient {
  @apply w-full h-full bottom-0;

  backdrop-filter: blur(10px);

  mask-image: linear-gradient(
    to top,
    rgba(244, 240, 235, 1) 65%,
    rgba(244, 240, 235, 0.95) 70%,
    rgba(244, 240, 235, 0.7) 80%,
    transparent
  );

  background: rgba(244, 240, 235, 1)
    linear-gradient(
      0,
      rgba(244, 240, 235, 0.8) 70%,
      rgba(244, 240, 235, 0.5) 80%,
      rgba(244, 240, 235, 0.3) 90%
    );

  @media (min-width: theme('screens.md')) {
    @apply w-[88%] left-0;

    backdrop-filter: blur(0);
    mask-image: none;

    background: linear-gradient(
      90deg,
      rgba(244, 240, 235) 60%,
      rgba(244, 240, 235, 0.7) 70%,
      rgba(244, 240, 235, 0.3) 80%,
      transparent 100%
    );
  }
}

.blackGradient {
  @apply h-full w-full bottom-0;

  backdrop-filter: blur(10px);

  mask-image: linear-gradient(
    to top,
    rgba(15, 17, 23, 1) 65%,
    rgba(15, 17, 23, 0.85) 70%,
    rgba(15, 17, 23, 0.5) 80%,
    transparent
  );

  background: rgba(15, 17, 23, 1)
    linear-gradient(
      0,
      rgba(15, 17, 23, 0.8) 70%,
      rgba(15, 17, 23, 0.5) 80%,
      rgba(15, 17, 23, 0.3) 90%,
      transparent 100%
    );

  @media (min-width: theme('screens.md')) {
    @apply w-[88%] left-0;

    backdrop-filter: blur(0);
    mask-image: none;

    background: linear-gradient(
      90deg,
      rgba(15, 17, 23) 60%,
      rgba(15, 17, 23, 0.7) 70%,
      rgba(15, 17, 23, 0.3) 80%,
      transparent 100%
    );
  }
}
</style>
